import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EpgStatus } from '@shared/types.ts';
import { api } from '@/lib/api.ts';
import { Badge, Problem, when } from './bits.tsx';

/**
 * THE GUIDE, made visible.
 *
 * A guide is downloaded lazily, held in memory for six hours and thrown away
 * on restart. That is a reasonable design and a completely opaque one: when
 * programmes look wrong there was nothing that could say whether the feed had
 * been fetched, what was in it, or whether the channel ids lined up — and no
 * way to ask for it again short of restarting the server.
 *
 * THE VERDICT LINE IS THE POINT OF THIS SCREEN. Two very different faults
 * look identical from inside the guide grid, and each has a different fix:
 *
 *   the feed is STALE       — its last programme is in the past. The provider
 *                             stopped updating. Refreshing cannot help; the
 *                             document you would download is the same one.
 *   the ids DO NOT MATCH    — the feed is current but almost none of your
 *                             channels carry an epg id it declares, so every
 *                             row falls back to filler.
 *
 * Everything else on the card is the evidence behind that sentence.
 */
export function Guide() {
  const { data: feeds, isLoading, error } = useQuery({
    queryKey: ['admin', 'epg'],
    queryFn: () => api.adminEpg(),
    /* The endpoint reads memory and never fetches, so asking often is free —
       and while a refresh runs, this is what shows it landing. */
    staleTime: 5_000,
  });

  if (isLoading) return <div className="muted center">Reading…</div>;
  if (error) return <Problem error={error} />;

  return (
    <>
      <p className="muted admin-note">
        Guides are downloaded when something first asks for one, kept in memory and
        re-fetched once the cache expires. Nothing is written to disk, so a restart
        starts over. Only Xtream lines and playlists with a guide URL appear here —
        iHeart and SiriusXM carry their own now-playing and have no XMLTV.
      </p>

      <div className="provider-list">
        {(feeds ?? []).map((f) => <FeedCard key={f.providerId} feed={f} />)}
        {feeds?.length === 0 && (
          <p className="muted">No provider on this server can carry a guide.</p>
        )}
      </div>
    </>
  );
}

function FeedCard({ feed: f }: { feed: EpgStatus }) {
  const qc = useQueryClient();

  const refresh = useMutation({
    mutationFn: () => api.adminEpgRefresh(f.providerId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'epg'] }); },
  });

  return (
    <section className="provider">
      <header className="provider-head">
        <div className="provider-who">
          <div className="admin-name">{f.providerName}</div>
          <div className="muted admin-sub">
            <Badge>{f.kind === 'm3u' ? 'playlist guide' : 'Xtream guide'}</Badge>{' '}
            {f.hasGuide
              ? <Badge tone="good">guide URL set</Badge>
              : <Badge tone="warn">no guide URL</Badge>}
          </div>
          <Verdict feed={f} />
        </div>

        <div className="provider-actions">
          <button
            className="btn primary"
            disabled={!f.hasGuide || refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            {/* These documents run to tens of megabytes. Saying so beats a
                button that looks stuck for forty seconds. */}
            {refresh.isPending ? 'Downloading…' : 'Refresh now'}
          </button>
        </div>
      </header>

      {refresh.isPending && (
        <p className="muted admin-field-hint">
          Downloading the whole document — this can take most of a minute.
        </p>
      )}
      <Problem error={refresh.error} />
      {f.lastError && (
        <p className="admin-error">
          Last fetch failed {when(f.lastFailureAt)}: {f.lastError}
        </p>
      )}

      <div className="stat-grid epg-stats">
        <Cell label="Last fetched" value={when(f.fetchedAt)}
          sub={f.fetchedAt ? `${mb(f.bytes)} · cached ${f.ttlHours}h` : 'nothing has asked yet'} />
        <Cell label="Programmes" value={num(f.programmes)}
          sub={`${num(f.indexedProgrammes)} in the near-term index`} />
        <Cell label="Guide covers" value={coverage(f)} sub={span(f)} />
        <Cell
          label="Channels matched"
          value={`${f.channelsMatched} of ${f.channelsWithEpgId}`}
          sub={`${f.channels} on this account · ${num(f.guideChannels)} in the feed`}
        />
      </div>
    </section>
  );
}

/**
 * One sentence saying what is wrong, or that nothing is.
 *
 * Ordered by which fault makes the others unreadable: with no document at all
 * every other number is zero and means nothing, and a stale document makes
 * the match count meaningless because nothing in it is current anyway.
 */
function Verdict({ feed: f }: { feed: EpgStatus }) {
  if (!f.hasGuide) {
    return (
      <p className="epg-verdict is-warn">
        No guide address, so these channels can only ever show filler.
        {f.kind === 'm3u'
          ? ' Add an XMLTV URL on the Providers tab.'
          : ' The Xtream line needs its host and password.'}
      </p>
    );
  }

  if (!f.fetchedAt) {
    return (
      <p className="epg-verdict">
        Not fetched since the server started. Open the guide, or press Refresh.
      </p>
    );
  }

  if (f.programmes === 0) {
    return (
      <p className="epg-verdict is-warn">
        The document downloaded but contains no programmes at all.
      </p>
    );
  }

  /* THE STALE CASE. `coversHours` is measured from the document's LAST
     programme to now, so a negative number is not "expiring soon" — it is a
     feed whose newest entry is already in the past. */
  if (f.coversHours !== null && f.coversHours < 0) {
    return (
      <p className="epg-verdict is-bad">
        <strong>This feed is stale.</strong> Its most recent programme
        starts {ago(f.coversHours)} — the provider has stopped updating it.
        Refreshing downloads the same document; this is fixed at their end.
      </p>
    );
  }

  if (f.coversHours !== null && f.coversHours < 6) {
    return (
      <p className="epg-verdict is-warn">
        The guide only reaches {f.coversHours}h ahead, so the grid runs out shortly.
      </p>
    );
  }

  if (f.channelsWithEpgId === 0) {
    return (
      <p className="epg-verdict is-warn">
        The feed is current, but none of these channels carry a guide id — so every
        row falls back to filler. Re-importing from the Providers tab restamps them.
      </p>
    );
  }

  if (f.channelsMatched === 0) {
    return (
      <p className="epg-verdict is-bad">
        <strong>The ids do not match.</strong> The feed is current and your channels
        have guide ids, but none of them appear in it — so every row falls back to
        filler. This is what happens after a provider renumbers its guide.
      </p>
    );
  }

  if (f.channelsMatched < f.channelsWithEpgId * 0.5) {
    return (
      <p className="epg-verdict is-warn">
        Only {f.channelsMatched} of {f.channelsWithEpgId} channels match something in
        the feed. The rest will show filler.
      </p>
    );
  }

  return (
    <p className="epg-verdict is-good">
      Healthy — current through {new Date(f.latestUtc!).toLocaleDateString()},
      {' '}{f.channelsMatched} of {f.channelsWithEpgId} channels matched.
    </p>
  );
}

function Cell({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="muted stat-sub">{sub}</div>}
    </div>
  );
}

function num(n: number): string { return n.toLocaleString(); }

function mb(bytes: number): string {
  if (!bytes) return '0 KB';
  return bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

function coverage(f: EpgStatus): string {
  if (f.coversHours === null) return '—';
  if (f.coversHours < 0) return 'expired';
  if (f.coversHours < 48) return `${f.coversHours}h ahead`;
  return `${Math.round(f.coversHours / 24)}d ahead`;
}

function span(f: EpgStatus): string | undefined {
  if (!f.earliestUtc || !f.latestUtc) return undefined;
  const a = new Date(f.earliestUtc).toLocaleDateString();
  const b = new Date(f.latestUtc).toLocaleDateString();
  return a === b ? a : `${a} → ${b}`;
}

/** A negative coversHours, said the way a person would say it. */
function ago(hours: number): string {
  const h = Math.abs(hours);
  if (h < 48) return `${h} hours ago`;
  const days = Math.round(h / 24);
  if (days < 60) return `${days} days ago`;
  return `about ${Math.round(days / 30)} months ago`;
}
